import { useMemo, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import Svg, { Circle, Polyline } from "react-native-svg";

import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";
import type { ProgressTrendPoint } from "@/types";

type MetricKey = "practice" | "goals" | "reflections";

const METRICS: { key: MetricKey; labelKey: string }[] = [
  { key: "practice", labelKey: "growth.trend.metrics.practice" },
  { key: "goals", labelKey: "growth.trend.metrics.goals" },
  { key: "reflections", labelKey: "growth.trend.metrics.reflections" },
];

const BAR_SCALE = 86;
const BAR_MIN = 2;
const PLOT_HEIGHT = 112;
const BAR_GAP = 4;

export function TrendChart({
  points,
  unit,
}: {
  points: ProgressTrendPoint[];
  unit: "day" | "week";
}) {
  const c = useTheme();
  const [metric, setMetric] = useState<MetricKey>("practice");
  const [plotWidth, setPlotWidth] = useState(0);
  const values = useMemo(
    () => points.map((point) => Number(point[metric]) || 0),
    [metric, points],
  );
  const total = values.reduce((sum, value) => sum + value, 0);
  const max = Math.max(1, ...values);
  const count = points.length;
  const columnWidth =
    count > 0 ? (plotWidth - BAR_GAP * (count - 1)) / count : 0;
  const heightFor = (value: number) =>
    Math.round((Math.max(0, value) / max) * BAR_SCALE) + BAR_MIN;
  const centerX = (index: number) =>
    index * (columnWidth + BAR_GAP) + columnWidth / 2;
  const yFor = (value: number) => PLOT_HEIGHT - heightFor(value);
  const linePoints = values
    .map((value, index) => `${centerX(index)},${yFor(value)}`)
    .join(" ");
  const selectedLabel = i18n.t(
    METRICS.find((item) => item.key === metric)?.labelKey ??
      "growth.trend.metrics.fallback",
  );
  const sum = (key: keyof Omit<ProgressTrendPoint, "bucketStart">) =>
    points.reduce((totalValue, point) => totalValue + point[key], 0);
  const context =
    metric === "goals"
      ? i18n.t("growth.trend.context.goals", {
          started: sum("goalsStarted"),
          completed: sum("goalsCompleted"),
          archived: sum("goalsArchived"),
        })
      : metric === "reflections"
        ? i18n.t("growth.trend.context.reflections")
        : i18n.t("growth.trend.context.practice");

  return (
    <View style={styles.wrap}>
      <View style={styles.tabs}>
        {METRICS.map((item) => {
          const active = item.key === metric;
          return (
            <Pressable
              key={item.key}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              onPress={() => setMetric(item.key)}
              style={[
                styles.tab,
                {
                  borderColor: active ? c.tint : c.border,
                  backgroundColor: active ? c.backgroundSelected : c.surfaceAlt,
                },
              ]}
            >
              <ThemedText
                variant="caption"
                tone={active ? "tint" : "secondary"}
              >
                {i18n.t(item.labelKey)}
              </ThemedText>
            </Pressable>
          );
        })}
      </View>

      {total === 0 ? (
        <View
          style={[
            styles.empty,
            { backgroundColor: c.surfaceAlt, borderColor: c.border },
          ]}
        >
          {/*
            Reachable in production, which is why these are catalogued rather
            than left inline: the caller only renders this chart when
            `points.length > 0`, but the empty state is gated on `total === 0`
            — the sum for the SELECTED metric — so switching the metric toggle
            to one with no data shows it.
          */}
          <ThemedText variant="bodyStrong">
            {i18n.t("growth.dashboard.trendNoDataTitle")}
          </ThemedText>
          <ThemedText variant="caption" tone="secondary">
            {i18n.t("growth.dashboard.trendNoDataBody")}
          </ThemedText>
        </View>
      ) : null}

      <View
        style={styles.plot}
        onLayout={(event) => setPlotWidth(event.nativeEvent.layout.width)}
      >
        <View style={styles.bars}>
          {points.map((point, index) => {
            const value = values[index];
            return (
              <View key={point.bucketStart} style={styles.column}>
                <ThemedText variant="caption" tone="muted">
                  {value > 0 ? value : ""}
                </ThemedText>
                <View
                  style={[
                    styles.bar,
                    {
                      height: heightFor(value),
                      backgroundColor:
                        index === count - 1
                          ? c.tint
                          : c.backgroundSelected,
                    },
                  ]}
                />
              </View>
            );
          })}
        </View>
        {count >= 2 && plotWidth > 0 ? (
          <Svg
            style={StyleSheet.absoluteFill}
            width={plotWidth}
            height={PLOT_HEIGHT}
            pointerEvents="none"
          >
            <Polyline
              points={linePoints}
              fill="none"
              stroke={c.tintStrong}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {values.map((value, index) => (
              <Circle
                key={points[index].bucketStart}
                cx={centerX(index)}
                cy={yFor(value)}
                r={3.5}
                fill={c.tintStrong}
              />
            ))}
          </Svg>
        ) : null}
      </View>
      <View style={styles.axis}>
        {points.map((point) => (
          <ThemedText
            key={point.bucketStart}
            variant="caption"
            tone="muted"
            numberOfLines={1}
            style={styles.axisLabel}
          >
            {bucketLabel(point.bucketStart)}
          </ThemedText>
        ))}
      </View>

      {total > 0 ? (
        <>
          <ThemedText variant="caption" tone="secondary" center>
            {total} {selectedLabel.toLocaleLowerCase(i18n.resolvedLanguage)}{" "}
            moments across {count} {unit === "day" ? "days" : "weeks"}
          </ThemedText>
          <ThemedText variant="caption" tone="muted" center>
            {context}
          </ThemedText>
        </>
      ) : null}
    </View>
  );
}

function bucketLabel(iso: string): string {
  return new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    day: "numeric",
    month: "short",
  }).format(new Date(iso));
}

const styles = StyleSheet.create({
  wrap: { gap: Spacing.md, minWidth: 0 },
  tabs: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.xs,
  },
  tab: {
    minHeight: 36,
    paddingHorizontal: Spacing.md,
    justifyContent: "center",
    borderRadius: Radius.md,
    borderWidth: 1,
  },
  empty: {
    gap: 3,
    padding: Spacing.lg,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  plot: { position: "relative", height: PLOT_HEIGHT },
  bars: {
    height: PLOT_HEIGHT,
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: BAR_GAP,
  },
  column: {
    flex: 1,
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
  },
  bar: { width: "70%", minWidth: 8, borderRadius: Radius.sm },
  axis: { flexDirection: "row", justifyContent: "space-between", gap: 4 },
  axisLabel: { flex: 1, fontSize: 9, textAlign: "center" },
});
